import { useEffect, useMemo, useState } from 'react';
import { Search, X, FileDown, Eye, Trash2, ChevronsUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { format } from 'date-fns';
import { Button, Input, Select, Card, CardContent, Badge } from '../../ui';
import Paginacion from '../../comunes/Paginacion';
import ConfirmacionEliminar from '../../ui/ConfirmacionEliminar';
import { listarAlumnos, eliminarAlumno, exportarAlumnosXlsx, getCursos, getAreas } from '../api';
import { useFormAuth } from '../../laeformacion/FormacionAccess';
import SepeAlumnoPotencialDialog from './SepeAlumnoPotencialDialog';

const SEPE_THEME = { '--brand-dark': '21 90% 42%', '--brand-light': '25 95% 53%' };

const ESTADOS = ['nuevo', 'contactado', 'matriculado', 'descartado'];
const ESTADO_LABEL = { nuevo: 'Nuevo', contactado: 'Contactado', matriculado: 'Matriculado', descartado: 'Descartado' };
const ESTADO_BADGE = { nuevo: 'info', contactado: 'warning', matriculado: 'success', descartado: 'secondary' };

const fmtFecha = (d) => (d ? format(new Date(d), 'dd/MM/yyyy HH:mm') : '—');

export default function SepeAlumnosPotenciales() {
  const { can } = useFormAuth();
  const canManage = can('formacion.sepe.alumnos.manage');

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState('');
  const [qDeb, setQDeb] = useState('');
  const [fEstado, setFEstado] = useState('');
  const [fCurso, setFCurso] = useState('');
  const [fArea, setFArea] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [ordenColumna, setOrdenColumna] = useState('created_at');
  const [ordenDir, setOrdenDir] = useState('desc');

  const [cursos, setCursos] = useState([]);
  const [areas, setAreas] = useState([]);

  const [selected, setSelected] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  useEffect(() => {
    getCursos().then(({ data }) => setCursos(data)).catch(() => {});
    getAreas().then(({ data }) => setAreas(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // reset a página 1 al cambiar filtros u orden
  useEffect(() => { setPage(1); }, [qDeb, fEstado, fCurso, fArea, desde, hasta, limit, ordenColumna, ordenDir]);

  // Click en cabecera: misma columna alterna asc/desc; nueva columna arranca
  // asc (salvo fecha, que arranca desc).
  const toggleOrden = (col) => {
    if (ordenColumna === col) {
      setOrdenDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrdenColumna(col);
      setOrdenDir(col === 'created_at' ? 'desc' : 'asc');
    }
  };

  const thOrden = (col, label, extra = '') => {
    const active = ordenColumna === col;
    const Icon = !active ? ChevronsUpDown : (ordenDir === 'asc' ? ChevronUp : ChevronDown);
    return (
      <th className={`px-4 py-2.5 ${extra}`}>
        <button
          type="button"
          onClick={() => toggleOrden(col)}
          className={`inline-flex items-center gap-1 uppercase tracking-wide ${active ? 'text-slate-700' : 'hover:text-slate-700'}`}
        >
          {label}
          <Icon className={`w-3.5 h-3.5 ${active ? 'text-slate-500' : 'text-slate-300'}`} />
        </button>
      </th>
    );
  };

  const filtros = useMemo(() => {
    const p = {};
    if (qDeb.trim()) p.q = qDeb.trim();
    if (fEstado) p.estado = fEstado;
    if (fCurso) p.curso_id = fCurso;
    if (fArea) p.area_id = fArea;
    if (desde) p.desde = desde;
    if (hasta) p.hasta = hasta;
    return p;
  }, [qDeb, fEstado, fCurso, fArea, desde, hasta]);

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await listarAlumnos({ ...filtros, orderBy: ordenColumna, order: ordenDir, page, limit });
      setRows(data.rows || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error('No se pudieron cargar los alumnos potenciales.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filtros, page, limit, ordenColumna, ordenDir]);

  const exportar = async () => {
    try {
      const blob = await exportarAlumnosXlsx(filtros);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alumnos-potenciales-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar el Excel.');
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminarAlumno(aEliminar.id);
      setAEliminar(null);
      toast.success('Alumno eliminado');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const limpiar = () => { setQ(''); setFEstado(''); setFCurso(''); setFArea(''); setDesde(''); setHasta(''); };

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full" style={SEPE_THEME}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">Alumnos potenciales SEPE</h1>
          <p className="text-sm text-slate-500 mt-1">Inscripciones públicas a cursos SEPE · <span className="font-medium text-slate-700">{count} alumnos</span></p>
        </div>
        <Button variant="secondary" onClick={exportar}><FileDown className="w-4 h-4 mr-1.5" /> Exportar Excel</Button>
      </div>

      {/* Filtros */}
      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[220px]">
              <Input
                prefix={<Search className="w-4 h-4" />}
                suffix={q ? <button onClick={() => setQ('')}><X className="w-4 h-4" /></button> : null}
                placeholder="Buscar por nombre, apellidos o email…"
                value={q} onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Select className="w-44" value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
              <option value="">Todos los estados</option>
              {ESTADOS.map((s) => <option key={s} value={s}>{ESTADO_LABEL[s]}</option>)}
            </Select>
            <Select className="w-48" value={fCurso} onChange={(e) => setFCurso(e.target.value)}>
              <option value="">Todos los cursos</option>
              {cursos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
            <Select className="w-40" value={fArea} onChange={(e) => setFArea(e.target.value)}>
              <option value="">Todas las áreas</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </Select>
            <Input type="date" className="w-40" value={desde} onChange={(e) => setDesde(e.target.value)} title="Desde" />
            <Input type="date" className="w-40" value={hasta} onChange={(e) => setHasta(e.target.value)} title="Hasta" />
            {(q || fEstado || fCurso || fArea || desde || hasta) && (
              <button onClick={limpiar} className="text-slate-400 hover:text-slate-700 text-sm px-2 py-2 flex items-center gap-1"><X className="w-4 h-4" /> Limpiar</button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide text-left">
              <tr>
                {thOrden('created_at', 'Fecha', 'w-40')}
                {thOrden('nombre', 'Nombre')}
                {thOrden('email', 'Contacto')}
                {thOrden('curso', 'Curso')}
                {thOrden('estado', 'Estado', 'w-32')}
                <th className="px-4 py-2.5 w-24 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">No hay alumnos potenciales que coincidan con los filtros.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)}>
                  <td className="px-4 py-2.5 text-slate-600">{fmtFecha(r.created_at || r.createdAt)}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.nombre} {r.apellidos}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-700">{r.email}</div>
                    <div className="text-xs text-slate-400">{r.telefono}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.curso ? (
                      <div className="flex flex-col">
                        <span className="text-slate-700">{r.curso.nombre}</span>
                        {r.curso.convocatoria && (
                          <span className="text-xs text-slate-400">{r.curso.convocatoria.nombre}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5"><Badge variant={ESTADO_BADGE[r.estado] || 'secondary'}>{ESTADO_LABEL[r.estado] || r.estado}</Badge></td>
                  <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <button className="text-slate-400 hover:text-slate-700 mr-2" title="Ver" onClick={() => setSelected(r)}><Eye className="w-4 h-4" /></button>
                    {canManage && (
                      <button className="text-slate-400 hover:text-rose-500" title="Eliminar" onClick={() => setAEliminar(r)}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
        <Paginacion
          page={page}
          pageSize={limit}
          total={count}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
        />
      </Card>

      <SepeAlumnoPotencialDialog
        open={!!selected}
        alumno={selected}
        onClose={() => setSelected(null)}
        onSaved={() => { cargar(); }}
        onDeleted={() => { setSelected(null); cargar(); }}
      />

      <ConfirmacionEliminar
        open={!!aEliminar}
        onClose={() => setAEliminar(null)}
        onConfirm={confirmarEliminar}
        titulo="Eliminar alumno potencial"
        descripcion="Esta acción no se puede deshacer. Se borrarán todos los datos del lead."
        palabraConfirmacion="ELIMINAR"
        textoBotonConfirmar="Eliminar definitivamente"
      />

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}
